/**
 * Flat file index over a `FileSystemDirectoryHandle`. Used by the chat
 * composer's `@` mention dropdown so the user can pick a file by name
 * instead of typing the full path. Honours `.gitignore` and the built-in
 * denylist so the index doesn't fill up with noise (`node_modules`, etc.).
 *
 * The walk is bounded — past `MAX_FILES` we stop indexing so a huge repo
 * doesn't lock the tab. Users searching beyond the cap will see "more
 * files exist; refine your search" in the dropdown.
 *
 * @module file-agent/lib/file-index
 */

import { compileGitignore, isIgnored } from "./gitignore";

/** Cap on the number of files we hold in memory for the mention picker. */
export const MAX_FILES = 5_000;

export interface IndexedFile {
  /** Filename only (e.g. "auth.ts"). */
  name: string;
  /** Path relative to the folder root (e.g. "src/lib/auth.ts"). */
  path: string;
}

export interface FileIndex {
  files: IndexedFile[];
  /** True when the walk hit MAX_FILES — UI should show a "refine search" hint. */
  truncated: boolean;
}

async function loadGitignore(root: FileSystemDirectoryHandle) {
  try {
    const handle = await root.getFileHandle(".gitignore");
    const file = await handle.getFile();
    return compileGitignore(await file.text());
  } catch {
    return [];
  }
}

/**
 * Walk the attached folder once and produce a flat file list for the
 * mention dropdown. Skips gitignored / denylisted entries; stops at
 * MAX_FILES.
 */
export async function buildFileIndex(
  root: FileSystemDirectoryHandle,
): Promise<FileIndex> {
  const patterns = await loadGitignore(root);
  const files: IndexedFile[] = [];
  let truncated = false;

  const visit = async (
    dir: FileSystemDirectoryHandle,
    relPath: string,
  ): Promise<void> => {
    if (truncated) return;
    const iter = dir as unknown as AsyncIterable<[string, FileSystemHandle]>;
    for await (const [name, child] of iter) {
      if (truncated) return;
      const childPath = relPath ? `${relPath}/${name}` : name;
      const isDir = child.kind === "directory";
      if (isIgnored(childPath, patterns, isDir)) continue;
      if (isDir) {
        await visit(child as FileSystemDirectoryHandle, childPath);
        continue;
      }
      files.push({ name, path: childPath });
      if (files.length >= MAX_FILES) {
        truncated = true;
        return;
      }
    }
  };

  await visit(root, "");
  files.sort((a, b) => a.path.localeCompare(b.path));
  return { files, truncated };
}

/**
 * Filter a file index against a user-typed query.
 *
 * Match strategy is intentionally simple — a case-insensitive substring
 * match on the path, with files whose *name* matches ranking before
 * those that match only via a parent segment. Good enough for picking
 * `auth.ts` from `src/lib/auth.ts` without pulling in a fuzzy library.
 */
export function filterFiles(
  index: FileIndex,
  query: string,
  limit = 20,
): IndexedFile[] {
  const q = query.trim().toLowerCase();
  if (!q) return index.files.slice(0, limit);

  const exact: IndexedFile[] = [];
  const nameMatch: IndexedFile[] = [];
  const pathMatch: IndexedFile[] = [];

  for (const f of index.files) {
    const lowerName = f.name.toLowerCase();
    if (lowerName === q) exact.push(f);
    else if (lowerName.includes(q)) nameMatch.push(f);
    else if (f.path.toLowerCase().includes(q)) pathMatch.push(f);
    if (exact.length + nameMatch.length + pathMatch.length >= limit * 2) break;
  }

  return [...exact, ...nameMatch, ...pathMatch].slice(0, limit);
}
